import { ChangeDetectionStrategy, Component, DestroyRef, inject, Injector, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrgSwitcherComponent, type OrgSwitcherOrg } from '@sbe-spinoff-client/app-shell';
import { firstValueFrom, noop } from 'rxjs';
import { SpinoffAuthService } from '../../auth/spinoff-auth.service';
import type { OrgSummaryDto } from '../../api-generated/models';
import { SpinoffActiveOrgContextService } from '../../orgs/spinoff-active-org-context.service';
import { SpinoffOrgInvitationsStore } from '../../orgs/spinoff-org-invitations.service';
import { SpinoffOrgsStore } from '../../orgs/spinoff-orgs.service';
import { SPINOFF_CREATE_ORG_DIALOG_LOADER } from './load-spinoff-create-org-dialog';

/**
 * 개인/단체 컨텍스트 전환 드롭다운 버튼("컨텍스트 스위처") — 툴바 좌측(로고 옆)에 배치한다.
 *
 * `SpinoffOrgContextButtonComponent`를 컨텍스트 스위처(이 컴포넌트)와 계정 관련 항목
 * (`SpinoffProfileMenuComponent`, 툴바 우측)으로 분리한 것 — GitHub 스타일 좌우 배치. 로그아웃·계정 항목은 여기 없다.
 *
 * 실제 렌더링(트리거 버튼·메뉴·개인/단체 항목·초대 배지)은 `@sbe-spinoff-client/app-shell`의
 * `OrgSwitcherComponent`(인증·스토어에 의존하지 않는 순수 프레젠테이션 컴포넌트)에 위임한다 —
 * 이 컴포넌트는 SpinoffPlatform의 서비스·스토어에서 값을 뽑아 넘기고 이벤트를 되받아 부수효과
 * (단체 목록 재시도 로딩, 초대 스낵바, 단체 만들기 다이얼로그)만 담당하는 얇은 어댑터다
 * (`SpinoffProfileMenuComponent` ↔ `ProfileMenuComponent`와 동일한 패턴). 외부 API
 * (selector·input·output)는 그대로 유지해 기존 소비 앱(sbe-sms/user-web,
 * sbe-commerce/user-web, sbe-schedule/web)은 변경 없이 그대로 동작한다.
 *
 * - `activeOrgId`: 현재 활성 단체 ID (null = 개인 모드).
 * - `orgSelected`: 단체 항목 클릭 또는 새 단체 생성 후 방출.
 * - `personalSelected`: "개인" 항목 클릭 시 방출.
 * - `invitationsRequested`: "받은 초대" 항목 클릭 또는 스낵바 액션 시 방출. 초대는 단체
 *   멤버십 개념이라 프로필이 아니라 이 컴포넌트가 책임진다.
 * - `personalContextEnabled`: 메뉴에 "개인" 항목 자체를 노출할지 여부(기본 true, 기존 앱은
 *   변경 없음). 모든 화면이 org 스코프라 개인 모드가 아무 콘텐츠도 없는 앱(예: sbe-schedule)은
 *   false로 꺼 혼란스러운 항목을 감춘다 — 앱별 opt-out, 기본은 하위호환 유지.
 * - `teamsEnabled`: 단체 항목 아래에 소속 team(반) 이름을 표시할지 여부(기본 false — opt-in,
 *   2026-08-18). `SpinoffOrgsStore`가 이미 `OrgSummaryDto.teams`를 들고 있어(`orgs()`가 그대로
 *   `<sbe-org-switcher [orgs]>`로 흘러감) 이 wrapper는 값을 다시 매핑하지 않고 그대로 전달만
 *   한다 — `OrgSwitcherOrg.teams`가 옵셔널이라 `OrgSummaryDto`가 구조적으로 이미 대입 가능하다.
 *
 * 컴포넌트 자체는 `Router`/`ActivatedRoute`를 직접 참조하지 않는다 — 각 앱은 이 이벤트를
 * 받아 자체 네비게이션 로직을 실행한다. 다만 개인/단체 클릭 시점에
 * `SpinoffActiveOrgContextService.markPersonalSelected()`/`markOrgSelected()`는 직접
 * 호출한다 — 클릭과 앱의 내비게이션 완료(`NavigationEnd`) 사이 몇 프레임 동안 그 서비스의
 * 라우트 기반 orgId가 아직 이전 값이라, 클릭 시점에 미리 반영해 체크 표시가 지연 없이
 * 즉시 따라오게 한다. 서비스 쪽 문서 참고.
 */
@Component({
  selector: 'spinoff-org-switcher',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OrgSwitcherComponent],
  template: `
    <sbe-org-switcher
      [orgs]="orgs()"
      [activeOrgId]="activeOrgId()"
      [personalContextEnabled]="personalContextEnabled()"
      [currentUser]="currentUser()"
      [pendingInvitationsCount]="pendingCount()"
      [teamsEnabled]="teamsEnabled()"
      (orgSelected)="selectOrg($event)"
      (personalSelected)="selectPersonal()"
      (invitationsRequested)="invitationsRequested.emit()"
      (createOrgRequested)="openCreateOrgDialog()"
      (menuOpened)="prefetchCreateOrgDialog()"
    />
  `,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
export class SpinoffOrgSwitcherComponent {
  private readonly auth = inject(SpinoffAuthService);
  private readonly orgsStore = inject(SpinoffOrgsStore);
  private readonly invitationsStore = inject(SpinoffOrgInvitationsStore);
  private readonly activeContext = inject(SpinoffActiveOrgContextService);
  private readonly injector = inject(Injector);
  private readonly loadCreateOrgDialog = inject(SPINOFF_CREATE_ORG_DIALOG_LOADER);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeOrgId = input<string | null>(null);
  readonly personalContextEnabled = input<boolean>(true);
  readonly teamsEnabled = input<boolean>(false);

  readonly orgSelected = output<OrgSummaryDto>();
  readonly personalSelected = output<void>();
  readonly invitationsRequested = output<void>();

  protected readonly currentUser = this.auth.currentUser;
  readonly orgs = this.orgsStore.orgs;
  readonly pendingCount = this.invitationsStore.pendingCount;

  // refresh()는 Promise라 rxjs subscribe처럼 takeUntilDestroyed로 .then() 콜백 자체의
  // 실행을 막을 수 없다 — 이 플래그로 직접 가드한다. 없으면 컴포넌트가 파괴된 뒤(로그아웃
  // 등) 늦게 도착한 응답이 이미 사라진 화면에 대해 재시도를 계속 걸거나 스낵바를 띄운다.
  private destroyed = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
    });

    void this.refreshOrgsWithRetry();

    void this.invitationsStore
      .refresh()
      .then(async () => {
        if (this.destroyed) return;
        const count = this.invitationsStore.pendingCount();
        if (count > 0) {
          // 스낵바(snack-bar 모듈 약 14KB)는 미수락 초대가 있을 때만 필요해 동적 import 한다 — 정적 import 면 앱 초기 번들에 실린다.
          const { MatSnackBar } = await import('@angular/material/snack-bar');
          if (this.destroyed) return;
          const ref = this.injector
            .get(MatSnackBar)
            .open(`미수락 초대 ${count}건이 있습니다`, '확인', { duration: 6000 });
          ref
            .onAction()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.invitationsRequested.emit());
        }
      })
      .catch(noop);
  }

  /**
   * 이 스위처는 앱 셸에 항상 떠 있고(로그인 세션 내내 재마운트되지 않음) `/orgs` 페이지와
   * 달리 에러를 보여줄 UI도, 사용자가 누를 재시도 버튼도 없다 — 최초 refresh()가 실패하면
   * (로그인 직후 access token 하이드레이션과의 레이스, 네트워크 순단 등) `_orgs`가 세션
   * 내내 빈 배열로 영구 고정돼 단체가 하나도 안 보이는 채로 남는다(2026-08-11, staging
   * native smoke에서 org 스위처 메뉴가 열려도 단체 항목이 끝내 안 뜨는 현상으로 실측 —
   * 원인을 100% 특정하진 못했지만, 이 무재시도 fail-silent 자체가 독립적으로 고쳐야 할
   * 결함이다). 짧은 간격으로 최대 2회만 추가 시도한다 — 무한 재시도로 영구 장애 시 요청을
   * 계속 쏘지 않는다.
   */
  private async refreshOrgsWithRetry(): Promise<void> {
    const RETRY_DELAYS_MS = [1_000, 3_000];
    for (const delayMs of [0, ...RETRY_DELAYS_MS]) {
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      if (this.destroyed) return;
      try {
        await this.orgsStore.refresh();
        return;
      } catch {
        // 다음 루프에서 재시도, 마지막 시도까지 실패하면 그냥 빈 목록으로 남는다 —
        // `/orgs` 페이지 방문 시 그쪽 refresh()가 다시 시도한다.
      }
    }
  }

  /**
   * 메뉴가 열리면 "단체 만들기" 다이얼로그 청크를 미리 받아 둔다 — 지연 로딩이라 첫 클릭 때 청크를 받느라 생기는 지연을
   * 없애기 위함이다. 실패해도 조용히 넘기고(클릭 시 `openCreateOrgDialog()`가 다시 시도한다) 이미 받았으면 캐시된 모듈을 쓴다.
   */
  protected prefetchCreateOrgDialog(): void {
    void this.loadCreateOrgDialog().catch(noop);
  }

  async openCreateOrgDialog(): Promise<void> {
    // dialog 모듈(Material dialog + CDK dialog 약 28KB)도 다이얼로그 컴포넌트와 함께 처음 열 때만 받는다 — 컴포넌트 청크가 이미 이 모듈을 공유하므로
    // (prefetch 로 받아 두면) 이 import 는 즉시 해소된다.
    const [SpinoffCreateOrgDialogComponent, { MatDialog }] = await Promise.all([
      this.loadCreateOrgDialog(),
      import('@angular/material/dialog'),
    ]);
    // 청크를 받는 동안 컴포넌트가 파괴됐으면(로그아웃·라우트 이동) 다이얼로그를 열지 않는다.
    if (this.destroyed) return;
    const result = await firstValueFrom(
      this.injector.get(MatDialog).open(SpinoffCreateOrgDialogComponent).afterClosed(),
    );
    if (this.destroyed) return;
    if (result) {
      // 생성 자체는 다이얼로그 내부에서 SpinoffOrgsStore.createOrg()가 처리해 공유 스토어에
      // 이미 반영돼 있다(this.orgs는 그 스토어의 signal) — 여기서 다시 append하지 않는다.
      this.selectOrg(result);
    }
  }

  protected selectPersonal(): void {
    this.activeContext.markPersonalSelected();
    this.personalSelected.emit();
  }

  /**
   * 자식은 lib-local 타입(`OrgSwitcherOrg`)으로 방출하지만, 흐르는 객체는 위에서 `[orgs]`로
   * 넘긴 `OrgSummaryDto` 원본 참조 그대로다 — 소비 앱 계약(`orgSelected: OrgSummaryDto`)을
   * 유지하기 위해 여기서 다시 좁힌다.
   */
  protected selectOrg(org: OrgSwitcherOrg): void {
    this.activeContext.markOrgSelected();
    this.orgSelected.emit(org as OrgSummaryDto);
  }
}
